import { Roles } from '@common/types/user';
import { APP_PREFIX_PATH } from '@constants/common';
import { useAuthMeQuery } from '@services/auth';
import { cookies } from '@utils/cookies';
import { pathToVP } from '@utils/route';
import { isAfter } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const skipUserMigrationVerificationPaths = [
    APP_PREFIX_PATH + '/auth/link-account',
    APP_PREFIX_PATH + '/auth/migrate-account',
];

const useAuth = ({ disableUserMigrationVerification }: { disableUserMigrationVerification?: boolean } = {}) => {
    const { error, isLoading, data: user, isFetching } = useAuthMeQuery(undefined, { refetchOnMountOrArgChange: true });
    const location = useLocation();

    const shouldVerifyMigrationStatus = useMemo(
        () =>
            !disableUserMigrationVerification &&
            user &&
            user.keycloak_migration_date &&
            !user.uses_keycloak &&
            !skipUserMigrationVerificationPaths.includes(location.pathname),
        [user, disableUserMigrationVerification, location.pathname]
    );

    const migrationDate = useMemo(() => new Date(user?.keycloak_migration_date ?? ''), [user]);
    useEffect(() => {
        if (shouldVerifyMigrationStatus) {
            if (user && !isNaN(migrationDate.getTime()) && isAfter(new Date(), migrationDate)) {
                // Set a short-lived (5 minutes) cookie so the login page can show the migration warning
                cookies.set('keycloak_migration_required', 1, { maxAge: 300 });
                window.location.replace(pathToVP('/accounts/logout').toString());
            }
        }
    }, [shouldVerifyMigrationStatus, user, migrationDate]);

    const checkUserPerms = (roles: Roles[]) => (user?.role ? roles.includes(user.role as Roles) : false);

    return {
        user,
        isUserLoading: isLoading,
        isLoggedIn: !isLoading && !!!error,
        error,
        checkUserPerms,
        isFetching,
        migrationDate,
    };
};

export { useAuth };
